import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import useCoveoCommerceExtras from "@DSG/Components/Coveo/useCoveoCommerceExtras";
import { PositionStyle } from "@DSG/MobiusOverrides/Popover/Popover";
import colorPallete, {
  bodyFonts,
  labelFonts,
} from "@DSG/MobiusOverrides/styles/variables";
import TextField, { TextFieldPresentationProps } from "@DSG/MobiusOverrides/TextField";
import { getSettingsCollection } from "@DSG/Overrides/Store/Context/ContextSelectors";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import { DISPATCH_ISLOADING_UI_STATE, DISPATCH_SELECTED_SUGGESTION_INDEX_STATE, DISPATCH_SET_SEARCH_INPUT_QUERY } from "@DSG/Store/Reducer/CoveoCommerceReducer";
import CoveoSearchPopover from "@DSG/Widgets/Coveo/Header/CoveoSearchPopover";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { addSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import React, { FocusEvent, useCallback, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: CoveoSearchInputStyles;
    shellContext?: any;
}

const mapStateToProps = (state: CoveoApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);

    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        isAuthenticated: state.context.session.isAuthenticated,
        coveoCommerce: state.DSGGlobalData,
        customSettings: getSettingsCollection(state).customSettings,
    };
};

const mapDispatchToProps = {
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CoveoSearchInputStyles {
    searchWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    input?: TextFieldPresentationProps;
}
    
export const coveoSearchInputStyles: CoveoSearchInputStyles = {
    searchWrapper: {
        css: css`
            position: relative;
        `
    },
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "rounded",
        cssOverrides: {
            formField: css`
                transition: all .2s ease-in-out;
            `,
            inputSelect: css`
                &[class*="Input"] {
                    border-color: ${colorPallete.grey400};
                    width: calc(100% - 40px);
                    min-height: 40px;
                    font-size: ${labelFonts.label2Regular.fontSize};
                    line-height: ${labelFonts.label2Regular.lineHeight};
                    &::placeholder {
                        color: ${colorPallete.grey600DisabledText};
                    }
                    
                    &:active,
                    &:focus {
                        border-width: 2px;
                    }
                    &,
                    &:active,
                    &:focus,
                    &:hover { 
                        padding: 8px 16px;
                        border-radius: 64px 0 0 64px;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            width: calc(100% - 56px);
                            min-height: 48px;
                            font-size: ${bodyFonts.body2RegularSm.fontSize};
                            line-height: ${bodyFonts.body2RegularSm.lineHeight};
                            &,
                            &:active,
                            &:focus,
                            &:hover { 
                                padding: 14px 16px 14px 24px;
                            }
                        `,
                    ], "min")}
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            &:focus {
                                font-size: ${bodyFonts.body1RegularLg.fontSize};
                                line-height: ${bodyFonts.body1RegularLg.lineHeight};
                            }
                            
                        `,
                    ], "max")}
                }
            `,
            formInputWrapper: css`
                display: flex;
                > [class*="FormFieldClickable"] {
                    position: relative;
                    height: 40px;
                    width: 40px;
                    border-radius: 0 64px 64px 0;
                    svg {
                        color: ${colorPallete.primaryRed500};
                    }
                    &,
                    &:active,
                    &:focus {
                        border: 1px solid ${colorPallete.grey400};
                        background: ${colorPallete.grey100};
                    }
                    &:hover {
                        background: ${colorPallete.primaryMaroon500};
                        border-color: ${colorPallete.primaryMaroon500};
                        svg {
                            color: ${colorPallete.white};
                        }
                    }
                    > span {
                        height: auto;
                        width: auto;
                        right: 0;
                        left: 0;
                        top: auto;
                        padding: 0;
                        justify-content: center;
                    }
                    svg {
                        width: 24px;
                        height: 24px;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            height: 48px;
                            width: 56px;
                        `,
                    ], "min")}
                }
            `
        },
    },
};

const CoveoSearchInput = ({
    extendedStyles,
    coveoCommerce,
    customSettings,
    searchHistoryEnabled,
    searchHistoryLimit,
    location
}: Props) => {
    const settings = customSettings as any;
    if (!settings.enableCoveoSearch) { return null; }
    const dispatch = useDispatch();
    const {
        doCoveoSearch,
        searchInputRef,
    } = useCoveoCommerceExtras(coveoCommerce);
    
    const isSearchPage = (path?: string): boolean => {
        if (!path) {return false;}
        return path.includes('/search') || path.includes('/noSearchResult');
    };

    useEffect(() => {
        const isBrandPage = location?.pathname?.startsWith("/brands/");
        const isSearchResultsPage = isSearchPage(location?.pathname);
        if (!isSearchResultsPage && !isBrandPage) {
            clearSearchState();
            setLocalInputValue("");
            coveoSearchBox?.updateText("");
         } else if (isSearchResultsPage) {
            const currentQuery = coveoSearchBox?.state?.value || '';
            if (currentQuery) {
                setLocalInputValue(currentQuery);
                setQueryMemoized(currentQuery);
            }
        }
    }, [location?.pathname]);

    const searchId = `headerSearch`;
    const styles = mergeToNew(coveoSearchInputStyles, extendedStyles);
    const coveoSearchBox = coveoCommerce?.SearchBoxController;
    const coveoInstantProducts = coveoCommerce?.InstantProductsController;
    const coveoFilterSuggestions = coveoCommerce?.FilterSuggestionsController;
    const [showPopOverSuggestions, setShowPopOverSuggestions] = useState(false);
    const [lastKeywordSuggestion, setLastKeywordSuggestion] = useState<string | null>(null);
    const [isClearSearchClick, setisClearSearchClick] = useState(false);
    const [localInputValue, setLocalInputValue] = useState("");
    const popoverRef = useRef<HTMLUListElement>(null);
    const {width} = useWindowSize();

    const searchBoxState = coveoCommerce?.SearchBoxState;
    const query = coveoCommerce?.SearchInputQuery;

    const brandFilterSuggestions = coveoFilterSuggestions?.filterSuggestions.find(
      (item: any) => item.state.field === "ec_brand"
    );

    const setQueryMemoized = useCallback((query: string) => {
        dispatch({
            type: DISPATCH_SET_SEARCH_INPUT_QUERY,
            query
        });
    }, [dispatch]);

    const setSelectedSuggestionIndex = (query: number) => dispatch({
        type: DISPATCH_SELECTED_SUGGESTION_INDEX_STATE,
        query
    });

    const debouncedSetQuery = useCallback(
        debounce((value: string) => {
            setQueryMemoized(value);
            coveoSearchBox?.updateText(value.trim());
        }, 200),
        [setQueryMemoized, coveoSearchBox]
    );

    useEffect(() => {
        if ((searchBoxState?.suggestions || [])?.length > 0 || getSearchHistory().length > 0) {
            setShowPopOverSuggestions(true);
        }

        const IPQuery = searchBoxState?.suggestions?.[0]?.rawValue;
        if (!IPQuery || (IPQuery === lastKeywordSuggestion)) { return; }
        setLastKeywordSuggestion(IPQuery);
        coveoInstantProducts?.updateQuery(IPQuery);
    }, [searchBoxState?.suggestions]);    
    
    useEffect(() => {
        return () => {
            debouncedSetQuery.cancel();
        };
    }, [debouncedSetQuery]);

    const clearSearchState = () => {
        setLastKeywordSuggestion(null);
        setShowPopOverSuggestions(false);
        setSelectedSuggestionIndex(0);
        dispatch({
            type: DISPATCH_SET_SEARCH_INPUT_QUERY,
            query: ''
        });
    };

    useEffect(() => {
        if (query !== localInputValue) {
            if (!query && localInputValue) {
                clearSearchState();
            }
            else if(isClearSearchClick){
                clearSearchState();
                setisClearSearchClick(false);
            } else {
                setLocalInputValue(query || "");
            }
        }
    }, [query]);

    const closeAutocomplete = (e?: FocusEvent<HTMLInputElement>) => {
        setSelectedSuggestionIndex(0);
        if (e && (popoverRef.current as any)?.element?.current?.contains(e.relatedTarget as Node)) { return; }
        (popoverRef?.current as any)?.closePopover();        
    };
    
    const doSearch = (q?: string) => {  
        if (query.trim() === "") { return; }
        dispatch({
            type: DISPATCH_ISLOADING_UI_STATE,
            query: false
        });
        // if (q) { setQueryMemoized(q); }
        // if (searchHistoryEnabled) { addSearchHistory(query, searchHistoryLimit); }
        const searchQuery = q || query;
        setQueryMemoized(searchQuery);
        if (searchHistoryEnabled) { addSearchHistory(searchQuery, searchHistoryLimit); }
        if (q) {
            // setLocalInputValue(q);
            // coveoCommerce?.SearchBoxController?.updateText(q);
            // doCoveoSearch(q);
            setLocalInputValue(searchQuery);
            coveoCommerce?.SearchBoxController?.updateText(searchQuery);
            doCoveoSearch(searchQuery);
        }

        window.scrollTo(0, 0);
        closeAutocomplete();
        // Remove focus from search input after search
        searchInputRef.current?.blur();
    };

    const openAutocomplete = () => {
        (popoverRef.current as any)?.openPopover();
    };

    const handleOnFocus = () => {
        if (localInputValue.trim()) {
            if ((searchBoxState?.suggestions || [])?.length > 0) {
                setShowPopOverSuggestions(true);
                const IPQuery = searchBoxState?.suggestions?.[0]?.rawValue;
                coveoInstantProducts?.updateQuery(IPQuery ?? "");
            }
        } else {
            clearSearchState();
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        
        // Always update the local input value first
        setLocalInputValue(value);
        
        if (!value) {
            // Clear state if value is completely empty
            debouncedSetQuery.cancel();     
            clearSearchState();
            coveoSearchBox?.updateText("");
            setLocalInputValue("");
        } else {
            setSelectedSuggestionIndex(0);
            if (!(popoverRef.current as any)?.state?.open) { 
                openAutocomplete(); 
            }
            debouncedSetQuery(value);
        }

        if((value.length > 0 && width > 1023 && (!coveoCommerce?.SearchBoxState?.suggestions || coveoCommerce?.SearchBoxState.suggestions.length === 0))){
            closeAutocomplete();
        }
    };

    const handleClearSearch = () => {
        debouncedSetQuery.cancel();
        clearSearchState();
    };

    const closePopoverWindow = () => {
        clearSearchState();
        closeAutocomplete();
    };

    const TextInput = (
        <>
            <TextField
                {...styles.input}
                ref={searchInputRef}
                value={localInputValue}
                iconClickableProps={{ 
                    onClick: (event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation(); 
                        doSearch(searchInputRef.current?.value);
                    }
                }}
                onChange={e => handleInputChange(e)}
                onKeyPress={e => e.key === "Enter" && doSearch(searchInputRef.current?.value)}
                placeholder={translate("What can we help you find?")}
                autoComplete="off"
                clickableText={translate("submit search")}
                id={searchId}
                data-test-selector="headerSearchInputTextField"
                onFocus={() => {
                    coveoSearchBox?.updateText(query.trim() || "");
                    handleOnFocus();
                    openAutocomplete();
                }}
                displayClearIcon={localInputValue.length > 0}
                handleClearIconClick={(event) => {
                    debouncedSetQuery.cancel();
                    setisClearSearchClick(true);
                    handleClearSearch();
                    searchInputRef.current?.focus();
                    coveoSearchBox?.updateText("");
                    brandFilterSuggestions?.updateQuery("");
                    setLocalInputValue("");
                    openAutocomplete();
                }}
            />   
        </>
    );

    const searchInput = (
        <StyledWrapper {...styles.inputWrapper} className="searchInput">
            {TextInput}
        </StyledWrapper>
    );

    // useEffect(() => {
    //     if (typeof window !== 'undefined' && isSearchPage(location?.pathname) && !localInputValue && !isClearSearchClick) {
    //         const hash = window.location.hash;
    //         if (hash && hash.startsWith('#q=')) {
    //             const queryFromHash = decodeURIComponent(hash.substring(3).split('&')[0]);
    //             setLocalInputValue(queryFromHash);
    //             setQueryMemoized(queryFromHash);
    //             coveoSearchBox?.updateText(queryFromHash.trim());
    //         }
    //     } 
    // }, [location?.pathname, localInputValue, setQueryMemoized, coveoSearchBox, isSearchPage]);

    return (
        <StyledWrapper className="searchWrapper" {...styles.searchWrapper}>
            <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                {translate("Coveo Search")}
            </VisuallyHidden>
           
            <CoveoSearchPopover
                popoverRef={popoverRef}
                searchId={searchId}
                searchInput={searchInput}
                coveoSearchInputStyles={styles}
                closeAutocomplete={closeAutocomplete}
                query={query}
                showAutocomplete={showPopOverSuggestions}
                handleClearSearch={handleClearSearch}    
                closePopoverWindow={closePopoverWindow}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(CoveoSearchInput)));